import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext.jsx";
import { useI18n } from "../i18n/I18nContext.jsx";
import { usePageTitle } from "../context/PageTitleContext.jsx";
import api from "../api/client.js";

/* Map icons to Tabler Icons (ti) */
const Icon = {
  Dashboard: <i className="ti ti-layout-dashboard" style={{ fontSize: "18px" }}></i>,
  Paras: <i className="ti ti-file-description" style={{ fontSize: "18px" }}></i>,
  Users: <i className="ti ti-users" style={{ fontSize: "18px" }}></i>,
  Zones: <i className="ti ti-map-pin" style={{ fontSize: "18px" }}></i>,
  Submit: <i className="ti ti-pencil" style={{ fontSize: "18px" }}></i>,
  Reviews: <i className="ti ti-clipboard-check" style={{ fontSize: "18px" }}></i>,
  Notifications: <i className="ti ti-bell" style={{ fontSize: "18px" }}></i>,
  Activity: <i className="ti ti-history" style={{ fontSize: "18px" }}></i>,
  Analytics: <i className="ti ti-chart-bar" style={{ fontSize: "18px" }}></i>,
  SubmittedAnswers: <i className="ti ti-list-check" style={{ fontSize: "18px" }}></i>,
  Bell: <i className="ti ti-bell" style={{ fontSize: "18px" }}></i>,
  ClosedParas: <i className="ti ti-lock" style={{ fontSize: "18px" }}></i>,
  Logout: <i className="ti ti-power" style={{ fontSize: "14px" }}></i>,
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, changeLang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { title: overrideTitle } = usePageTitle();
  const [unread, setUnread] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (sidebarOpen) {
      document.body.classList.add("sb-locked");
    } else {
      document.body.classList.remove("sb-locked");
    }
    return () => document.body.classList.remove("sb-locked");
  }, [sidebarOpen]);

  // Poll the unread notification count.
  useEffect(() => {
    let active = true;
    const load = () =>
      api
        .get("/notifications/", { params: { is_read: false } })
        .then((r) => active && setUnread(r.data.count || 0))
        .catch(() => { });
    load();

    const handleUpdate = () => {
      load();
    };
    window.addEventListener("notifications_updated", handleUpdate);

    const id = setInterval(load, 30000);
    return () => {
      active = false;
      clearInterval(id);
      window.removeEventListener("notifications_updated", handleUpdate);
    };
  }, [location.pathname]);

  // Treat ZONAL_AC / DEPT_HEAD as acting Officer (personal login) when they
  // are NOT logged in as the official zone/department account.
  const isPersonalLogin =
    (user.role === "ZONAL_AC" && !user.is_zone_account) ||
    (user.role === "DEPT_HEAD" && !user.is_department_account);

  // CORP_ADMIN with a zone or department is scoped — hide zone/dept management pages.
  const isRestrictedCorpAdmin =
    user.role === "CORP_ADMIN" && (!!user.zone || !!user.department);

  const canSubmit = ["OFFICER", "ZONAL_AC", "DEPT_HEAD"].includes(user.role);
  const canReview = ["AUDITOR", "SUPER_ADMIN"].includes(user.role) && !isPersonalLogin;
  const canSeeAnalytics = ["SUPER_ADMIN", "CORP_ADMIN", "AUDITOR"].includes(user.role) && !isPersonalLogin;
  const canSeeZones = ["SUPER_ADMIN", "CORP_ADMIN", "AUDITOR"].includes(user.role) && !isPersonalLogin && !isRestrictedCorpAdmin;
  const canSeeOfficers =
    !isPersonalLogin && (
      ["SUPER_ADMIN", "CORP_ADMIN", "AUDITOR"].includes(user.role) ||
      (user.role === "ZONAL_AC" && user.is_zone_account)
    );
  const canManageDepartments = ["SUPER_ADMIN", "CORP_ADMIN"].includes(user.role) && !isPersonalLogin && !isRestrictedCorpAdmin;
  const canSeeSubmittedAnswers = ["OFFICER", "DEPT_HEAD"].includes(user.role) || isPersonalLogin;
  const canAddUser =
    !isPersonalLogin && (
      ["SUPER_ADMIN", "CORP_ADMIN"].includes(user.role) ||
      (user.role === "ZONAL_AC" && user.is_zone_account) ||
      (user.role === "DEPT_HEAD" && user.is_department_account)
    );

  const navGroups = [
    {
      section: t("navOverview"),
      items: [
        { to: "/", icon: Icon.Dashboard, label: t("dashboard"), end: true },
        { to: "/paras", icon: Icon.Paras, label: t("paras") },
        { to: "/closed-paras", icon: Icon.ClosedParas, label: t("closedParas") },
        ...(canSeeOfficers ? [{ to: "/officers", icon: Icon.Users, label: t("users") }] : []),
        ...(canAddUser ? [{ to: "/add-user", icon: <i className="ti ti-user-plus" style={{ fontSize: "18px" }}></i>, label: t("addNewUser") }] : []),
        ...(canSeeZones ? [{ to: "/zones", icon: Icon.Zones, label: t("zones") }] : []),
        ...(canManageDepartments ? [{ to: "/departments", icon: <i className="ti ti-building" style={{ fontSize: "18px" }}></i>, label: t("departments") }] : []),
      ],
    },
    {
      section: t("navWorkflow"),
      items: [
        ...(canSubmit ? [{ to: "/submit", icon: Icon.Submit, label: t("submitReply") }] : []),
        ...(canSeeSubmittedAnswers ? [{ to: "/submitted-answers", icon: Icon.SubmittedAnswers, label: t("submittedAnswers") }] : []),
        ...(canReview ? [{ to: "/reviews", icon: Icon.Reviews, label: t("reviews") }] : []),
        { to: "/notifications", icon: Icon.Notifications, label: t("notifications"), badge: unread },
        { to: "/activity", icon: Icon.Activity, label: t("activityLog") },
      ],
    },
    {
      section: t("navInsights"),
      items: [
        ...(canSeeAnalytics ? [{ to: "/analytics", icon: Icon.Analytics, label: t("analytics") }] : []),
      ],
    },
  ].filter((g) => g.items.length > 0);

  const titles = {
    "/": t("dashboard"),
    "/paras": t("paras"),
    "/closed-paras": t("closedParas"),
    "/submit": t("submitReply"),
    "/submitted-answers": t("submittedAnswers"),
    "/reviews": t("reviews"),
    "/analytics": t("analytics"),
    "/zones": t("zones"),
    "/officers": t("users"),
    "/departments": t("departments"),
    "/add-user": t("addNewUser"),
    "/notifications": t("notifications"),
    "/activity": t("activityLog"),
  };
  const pageTitle = overrideTitle
    || (location.pathname.startsWith("/paras/")
      ? t("paraDetail")
      : titles[location.pathname] || t("appShort"));

  const today = new Date().toLocaleDateString(lang === "ta" ? "ta-IN" : "en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const initials = (user.full_name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return (
    <div className={"app-shell" + (sidebarOpen ? " sb-open" : "")}>
      <div
        className="sb-backdrop"
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />
      <aside className="sb">
        <div className="sb-brand">
          <img src="/logo.png" alt="CCMC Logo" style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover", border: "1.5px solid rgba(255,255,255,0.4)" }} />
          <div className="sb-brand-text">
            <div className="sb-brand-title">{t("appShort")}</div>
          </div>
          <button
            className="sb-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
            type="button"
          >
            <i className="ti ti-x" style={{ fontSize: "18px" }}></i>
          </button>
        </div>
        <nav className="sb-nav">
          {navGroups.map((group) => (
            <div key={group.section}>
              <div className="sb-nav-section">{group.section}</div>
              {group.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    "sb-item" + (isActive ? " active" : "")
                  }
                >
                  {n.icon}
                  <span style={{ flex: 1 }}>{n.label}</span>
                  {n.badge > 0 && (
                    <span className="sb-badge">{n.badge}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="sb-user">
          <div className="sb-user-row">
            <div className="sb-avatar">{initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="sb-user-name" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user.full_name}</div>
              <div className="sb-user-role" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {user.role_display}
                {user.zone_name ? ` · ${user.zone_name}` : ""}
              </div>
            </div>
          </div>
          <button
            className="sb-logout"
            onClick={logout}
            title={t("logout")}
            aria-label={t("logout")}
          >
            {Icon.Logout} {t("logout")}
          </button>
        </div>
      </aside>

      <div className="main-area" style={{ position: "relative" }}>

        <header className="topbar" style={{ position: "relative", zIndex: 15 }}>
          <button
            className="sb-toggle"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
            type="button"
          >
            <i className="ti ti-menu-2" style={{ fontSize: "20px" }}></i>
          </button>
          <div className="topbar-title">
            {pageTitle}
            <small>{today} · CCMC Corporation</small>
          </div>
          <div className="topbar-actions" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div className="topbar-lang-group" style={{ display: "flex", gap: "2px", background: "#dbeafe", padding: "2px", borderRadius: "8px" }}>
              <button
                className="topbar-lang"
                style={lang === "en" ? { background: "#1e3a8a", color: "#fff", border: "none" } : { border: "none" }}
                onClick={() => changeLang("en")}
              >
                EN
              </button>
              <button
                className="topbar-lang"
                style={lang === "ta" ? { background: "#1e3a8a", color: "#fff", border: "none" } : { border: "none" }}
                onClick={() => changeLang("ta")}
              >
                த
              </button>
            </div>
            <button
              className="topbar-btn topbar-btn-cp"
              onClick={() => navigate("/change-password")}
            >
              <i className="ti ti-lock" style={{ fontSize: "13px" }}></i> <span className="topbar-btn-label">{t("changePassword")}</span>
            </button>
            <button
              className="topbar-btn"
              style={{ padding: "6px 9px", position: "relative" }}
              onClick={() => navigate("/notifications")}
              aria-label={t("notifications")}
            >
              {Icon.Bell}
              {unread > 0 && <span style={{ position: "absolute", top: "4px", right: "4px", width: "7px", height: "7px", background: "#ef4444", borderRadius: "50%" }} />}
            </button>
          </div>
        </header>
        <main className="content" style={{ position: "relative", zIndex: 1 }}>{children}</main>
      </div>
    </div>
  );
}
